import axios from "axios";
import { Slide, Typography } from "@mui/material";
import React from "react";
import { setAccessToken } from "accessToken";
import { dateMoment } from "components/Utilities/Time";
import { safeArray } from "./utils";

export const showErrorMsg = (enqueueSnackbar, errorMsg) => {
  enqueueSnackbar(
    <Typography style={{ fontSize: "1.2rem" }}>{errorMsg}</Typography>,
    {
      variant: "error",
      preventDuplicate: true,
      anchorOrigin: {
        horizontal: "center",
        vertical: "top",
      },
      autoHideDuration: 10000,
      TransitionComponent: Slide,
    },
  );
};

export const showSuccessMsg = (enqueueSnackbar, successMsg) => {
  enqueueSnackbar(
    <Typography style={{ fontSize: "1.2rem" }}>{successMsg}</Typography>,
    {
      variant: "success",
      preventDuplicate: true,
      anchorOrigin: {
        horizontal: "right",
        vertical: "top",
      },
      autoHideDuration: 5000,
      TransitionComponent: Slide,
    },
  );
};

export const getErrorMsg = (error) => {
  try {
    // First check graphQLErrors (common in both types)
    if (error?.graphQLErrors && error?.graphQLErrors?.length > 0) {
      const errMsgs = safeArray(error?.graphQLErrors)?.map(
        (err) => err?.message,
      );
      return errMsgs.join(", ");
    }

    // Check networkError and its nested result
    if (error?.networkError) {
      const networkError = error?.networkError;

      // Check if the network error has a result with GraphQL errors (common in 400 responses)
      if (
        networkError?.result?.errors &&
        networkError?.result?.errors?.length > 0
      ) {
        const errMsgs = safeArray(networkError.result.errors).map(
          (err) => err.message,
        );
        return errMsgs.join(", ");
      }

      // Return the network error message
      return networkError?.message || "Network Error!";
    }

    // Check for direct error message property
    if (error?.message) {
      return error.message;
    }

    // Check for errorMessage property (from ApolloErrorOptions)
    if (error?.errorMessage) {
      return error.errorMessage;
    }

    // If error is a direct error response object (like your backend response)
    if (
      error?.errors &&
      Array.isArray(error?.errors) &&
      error?.errors?.length > 0
    ) {
      const errMsgs = safeArray(error.errors).map((err) => err?.message);
      return errMsgs.join(", ");
    }

    return "Something went wrong. Try again!!!";
  } catch (err) {
    console.error("error from get error func.", err);
    return "Something went wrong. Try again!!!";
  }
};

const handleError = (error, enqueueSnackbar) => {
  showErrorMsg(enqueueSnackbar, getErrorMsg(error));
};

export const compressAndUploadImage = async (
  img,
  uploadFunc,
  setPreview,
  name,
  setFieldValue,
  setProgress,
  isCompressing,
  setIsCompleted,
) => {
  try {
    if (!img) throw new Error("No file passed to upload function");
    const uploadRes = await uploadFunc(img, setProgress);
    if (uploadRes === undefined) {
      throw new Error("couldn't upload image");
    }
    if (uploadRes) {
      setFieldValue(name, uploadRes);
      setIsCompleted("passed");
      setTimeout(() => {
        setIsCompleted(null);
      }, 1500);
    }
  } catch (error) {
    console.log("Error while trying to upload image", error);
    setProgress(100);
    setIsCompleted("failed");
    setTimeout(() => {
      setPreview(undefined);
      setIsCompleted(null);
    }, 1500);
  }
};

export const scrollToTop = (element) => {
  try {
    element?.current?.scrollIntoView({ behavior: "smooth" });
  } catch (error) {
    console.log("couldn't scroll to the top", error);
  }
};

export const uploadImage = async (file, setProgress) => {
  try {
    const token = process.env.REACT_APP_API_KEY;
    const form = new FormData();
    form.append("file", file);
    const data = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_BASE_URL}/rest/media`,
      headers: {
        "Content-Type": `multipart/form-data; boundary=${form._boundary}`,
        authorization: `Api-Key ${token}`,
      },
      data: form,
      onUploadProgress: (data) => {
        //Set the progress value to show the progress bar
        setProgress(Math.round((100 * data.loaded) / data.total));
      },
    });
    return data.data.data.mediaUrl; //data.data.mediaUrl
  } catch (error) {
    console.error(error);
    setProgress(100);
  }
};

export const getSelectedCertification = (obj) => {
  try {
    let trueArrary = [];
    Object.keys(obj).map((key) => {
      if (obj[key] === true) {
        trueArrary.push(key);
      }
    });
    const arrWithoutLicense = trueArrary.filter((value) => value !== "license");
    return arrWithoutLicense;
  } catch (error) {
    console.log("couldn't check if true is greater than 1", error);
  }
};

export const checkForIncompleteFields = (
  arr,
  values,
  requirements,
  isNonMedical,
) => {
  try {
    if (isNonMedical === false && arr.length === 0) {
      return false;
    }

    const required = arr.map((value) => {
      return requirements[value];
    });
    const flatArr = required.flat();
    const noEmptyFields = flatArr.every((field) => values[field] !== "");

    return noEmptyFields;
  } catch (error) {
    console.error("Error occured while checking for empty fields", error);
    return true;
  }
};

export const signUp = async (
  register,
  signUpData,
  setAccessToken,
  enqueueSnackbar,
  onsubmitProp,
) => {
  try {
    const { email, password, providerId, referralCode } = signUpData;
    const { data } = await register({
      variables: {
        email: email.toLowerCase(),
        password,
        providerId,
        referralCode,
      },
    });

    if (data) {
      const {
        dociId,
        email: emails,
        access_token,
        _id,
        refresh_token,
      } = data.signup.account;

      localStorage.setItem("email", emails);
      localStorage.setItem("heala_id", dociId);
      localStorage.setItem("account_id", _id);
      localStorage.setItem("token", access_token);
      setAccessToken(access_token);
      showSuccessMsg(enqueueSnackbar, "Sign up successsful");
      onsubmitProp.resetForm();
      return { success: true, data: data.signup.account };
    }
  } catch (error) {
    showErrorMsg(enqueueSnackbar, getErrorMsg(error));
    // if (
    //   error.networkError.result.errors[0].message === "Email is already taken"
    // ) {
    //   showErrorMsg(
    //     enqueueSnackbar,
    //     "Email already exist, please Sign in to continue.",
    //   );
    //   return "Email is already taken";
    // } else {
    //   handleError(error, enqueueSnackbar);
    //   return false;
    // }
  }
};

const getDocProfile = async (fetchProfile, dociId, email) => {
  const profileDataRes = await fetchProfile({
    variables: {
      dociId,
    },
  });

  const profileDataArr = profileDataRes?.data?.doctorProfiles.profile;

  const profileData = profileDataArr.filter(
    (profile) => profile?.email === email,
  );

  return profileData;
};

const getDocVerification = async (fetchVerification, id) => {
  const getVerificationRes = await fetchVerification({
    variables: {
      profileId: id,
    },
  });

  const verificationData =
    getVerificationRes.data.getVerifications.verification;

  return verificationData;
};

export const checkVerificationStatus = async (fetchVerification, id) => {
  const docVerificationRes = await getDocVerification(fetchVerification, id);

  if (docVerificationRes.length === 0) {
    return "NO VERIFICATION FOUND";
  }

  if (verificationData[0].status === false) {
    return "VERIFICATION PENDING"; // VERIFICATION PENDING
  }

  if (verificationData[0].status === true) {
    return "VERIFICATION COMPLETED"; // VERIFICATION COMPLETED
  }
};

export const signIn = async (
  loginFunc, //loginFunc
  loginData,
  enqueueSnackbar,
  fetchProfile,
  fetchVerification,
  onsubmitProp,
) => {
  try {
    const { email, password } = loginData;
    const { data } = await loginFunc({
      variables: {
        email: email.toLowerCase(),
        password,
      },
    });

    const {
      dociId,
      email: emails,
      access_token,
      _id,
      role,
    } = data.login.account;

    if (role !== "doctor") {
      showErrorMsg(
        enqueueSnackbar,
        "This email is already linked to a Heala/HMO/Hospital user account.",
      );
      return "STEP 1";
    }

    localStorage.setItem("email", emails);
    localStorage.setItem("account_id", _id);
    localStorage.setItem("heala_id", dociId);
    localStorage.setItem("token", access_token);
    setAccessToken(access_token);

    const profileData = await getDocProfile(fetchProfile, dociId, emails);

    if (profileData.length === 0) {
      showErrorMsg(
        enqueueSnackbar,
        "Profile not found. Please fill and submit the form below to create a profile",
      );
      return "STEP 2";
    }

    localStorage.setItem("profile_id", profileData[0]._id);

    const verificationData = await getDocVerification(
      fetchVerification,
      profileData[0]._id,
    );

    if (verificationData.length === 0) {
      showErrorMsg(
        enqueueSnackbar,
        "Verification not found or rejected. Please fill and submit the form below to create a verification process.",
      );
      return "STEP 3";
    }

    if (verificationData[0].status === false) {
      return "STEP 4"; // VERIFICATION PENDING
    }

    if (verificationData[0].status === true) {
      return "STEP 5"; // VERIFICATION COMPLETED
    }

    onsubmitProp.resetForm();
  } catch (err) {
    handleError(err, enqueueSnackbar);
  }
};

export const logOut = async (logoutUser, token) => {
  try {
    const res = await logoutUser({
      variables: {
        user: token,
      },
    });

    return res?.data?.logout?.result;
  } catch (error) {
    console.log("Error while trying to log out", error);
  }
};

export const onPageTwoFormSubmit = async (values, createDoctor) => {
  const {
    dob,
    firstName,
    lastName,
    gender,
    specialization,
    image,
    healaId,
    phoneNumber,
    hospital,
    cadre,
    providerId,
  } = values;
  const correctDOB = dateMoment(dob);
  try {
    if (!healaId) {
      showErrorMsg(enqueueSnackbar, "heala ID not found!");
      return;
    }
    const { data } = await createDoctor({
      variables: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        gender,
        specialization,
        image,
        phoneNumber,
        providerId,
        cadre,
        dociId: healaId,
        hospital: hospital.trim(),
        dob: correctDOB,
      },
    });
    const { _id } = data?.createDoctorProfile?.profile;
    localStorage.setItem("profile_id", _id);
    return { isSuccessful: true, data: data?.createDoctorProfile?.profile };
  } catch (err) {
    return { isSuccessful: false, error: err };
  }
};

export const onPageThreeSubmit = async (
  values,
  selectedCert,
  enqueueSnackbar,
  requirementValues,
  createVerification,
  handleNext,
  onsubmitProp,
  checked,
) => {
  const {
    degree,
    degreeImage,
    license,
    expire,
    licenseImage,
    licenseType,
    year,
    gYear,
    gImage,
    FacebookName,
    InstagramName,
    doctorName,
    referenceCode,
    doctorEmail,
    doctorPosition,
    doctorInstitution,
  } = values;

  const gradYear = dateMoment(gYear);
  const expires = dateMoment(expire);
  const mdYear = dateMoment(year);

  if (selectedCert.length === 0) {
    showErrorMsg(
      enqueueSnackbar,
      "Select at least 2 verification methods and fill the forms below",
    );
    return;
  }

  const incompleteFields = checkForIncompleteFields(
    selectedCert,
    values,
    requirementValues,
  );

  if (!incompleteFields) {
    showErrorMsg(
      enqueueSnackbar,

      "Empty fields detected, complete the form.",
    );
    return;
  }

  if (!checked) {
    showErrorMsg(
      enqueueSnackbar,
      "To continue, click on the box to accept our terms and conditiion.",
    );
    return;
  }

  try {
    // GET PROFILE_ID
    const { data } = await createVerification({
      variables: {
        degree, //
        image: degreeImage,
        year: mdYear,
        number: license,
        expiryDate: expires,
        licenseImage, //
        type: licenseType,
        graduation: gradYear == null ? "" : gradYear, //
        graduationImage: gImage,
        facebook: FacebookName,
        instagram: InstagramName,
        doctorName: doctorName,
        reference: referenceCode,
        profileId: localStorage.getItem("profile_id"),
        doctorEmail,
        doctorPosition, //
        doctorInstitution, //
      },
    });
    if (data) {
      return { isSuccessful: true, data };
    } else {
      return { isSuccessful: false };
    }
    // onsubmitProp.resetForm();
  } catch (err) {
    console.error(err);
    return { isSuccessful: false, error: err };
    // handleError(err, enqueueSnackbar);
  }
};

export const getYearsBack = (years) => {
  let yearsAgo = new Date();
  yearsAgo = yearsAgo.setFullYear(yearsAgo.getFullYear() - years);
  return yearsAgo;
};

export const pick = (obj, arrOfKeys) => {
  let newObj = {};
  arrOfKeys.forEach((key) => {
    if (typeof key === "object") {
      newObj[key[0]] = obj[key[0]] || key[1];
    } else {
      newObj[key] = obj[key] || "";
    }
  });
  return newObj;
};

export const getQulificationsOptions = (cadre) => {
  switch (`${cadre}`.toLowerCase()) {
    case "House officer".toLowerCase():
      return [
        {
          key: "MBBS Qualification",
          value: "MBBS Qualification",
        },
      ];

    case "Medical officer (MO)".toLowerCase():
      return [
        {
          key: "MBBS Qualification",
          value: "MBBS Qualification",
        },
      ];

    case "Registrar".toLowerCase():
      return [
        {
          key: "Primary Certificate",
          value: "Primary Certificate",
        },
      ];

    case "Senior Registrar".toLowerCase():
      return [
        {
          key: "Member Certificate",
          value: "Member Certificate",
        },
      ];

    case "Consultant".toLowerCase():
      return [
        {
          key: "Fellow Certificate",
          value: "Fellow Certificate",
        },
      ];
    default:
      return [
        {
          key: "MBBS Qualification",
          value: "MBBS Qualification",
        },
        {
          key: "Primary Certificate",
          value: "Primary Certificate",
        },
        {
          key: "Member Certificate",
          value: "Member Certificate",
        },
        {
          key: "Fellow Certificate",
          value: "Fellow Certificate",
        },
      ];
  }
};

export const formatPhoneNumber = (phoneNumber) => {
  try {
    return phoneNumber.replace(/^(\+234)/g, "");
  } catch (error) {
    console.error(error);
    return phoneNumber;
  }
};

export const getProviderOptions = async (fetchProviders, varibles) => {
  try {
    const { data } = await fetchProviders({ variables: varibles });
    const options = (data?.getProviders?.provider || []).map((option) => {
      return { value: option?._id, key: option?.name };
    });
    return options;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const deleteVar = (variable) => {
  try {
    for (const key in variable) {
      delete variable[key];
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("couldn't delete variables", error);
  }
};
